// Login page — split layout, india map watermark
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Box, TextField, Button, Alert, CircularProgress, Chip } from "@mui/material";
import { LogIn } from "lucide-react";
import { useAuth } from "../components/AuthContext";
import { formatApiError } from "../api/api";

const DEMO_CREDS = [
  { role: "Chief Risk Officer", email: "admin@climatebank.in", password: "Admin@1234" },
  { role: "Senior Risk Analyst", email: "risk@climatebank.in", password: "Risk@1234" },
  { role: "Credit Officer", email: "credit@climatebank.in", password: "Credit@1234" },
  { role: "Branch Manager", email: "branch@climatebank.in", password: "Branch@1234" },
];

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e?.preventDefault();
    setError(""); setLoading(true);
    try {
      await login(email.trim(), password);
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiError(err));
    } finally { setLoading(false); }
  };

  const applyDemo = async (d) => {
    setEmail(d.email); setPassword(d.password);
    setError(""); setLoading(true);
    try {
      await login(d.email, d.password);
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiError(err));
    } finally { setLoading(false); }
  };

  return (
    <Box className="auth-bg" sx={{ minHeight: "100vh", display: "grid", gridTemplateColumns: { xs: "1fr", md: "1.05fr 1fr" }, position: "relative" }}>
      {/* LEFT: hero + watermark */}
      <Box sx={{
        position: "relative", display: { xs: "none", md: "flex" }, flexDirection: "column",
        justifyContent: "space-between", p: 6, background: "#FFFFFF",
        borderRight: "1px solid #E2E8F0", overflow: "hidden",
      }}>
        <div className="auth-watermark" />
        <Box sx={{ position: "relative", zIndex: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.4 }}>
            <Box sx={{
              width: 36, height: 36, borderRadius: 1, background: "linear-gradient(135deg, #1E2A38 0%, #2E5A35 100%)",
              display: "flex", alignItems: "center", justifyContent: "center", color: "#A3C2A9", fontWeight: 700,
              fontFamily: "Playfair Display, serif", fontSize: 20,
            }}>⏚</Box>
            <Box>
              <Box className="cb-display" sx={{ fontSize: 19, fontWeight: 600, color: "#111827" }}>
                ClimateLedger
              </Box>
              <Box sx={{ fontSize: 10, letterSpacing: "0.16em", color: "#6B7280", textTransform: "uppercase", fontFamily: "IBM Plex Mono, monospace" }}>
                Banking Physical Risk
              </Box>
            </Box>
          </Box>
        </Box>

        <Box sx={{ position: "relative", zIndex: 1, maxWidth: 480 }}>
          <Box className="cb-display" sx={{ fontSize: { md: 38, lg: 46 }, lineHeight: 1.08, color: "#111827", mb: 2 }}>
            Physical climate risk, mapped to every collateral on your book.
          </Box>
          <Box sx={{ fontSize: 14, color: "#4B5563", lineHeight: 1.55, mb: 3 }}>
            PIN-code level geospatial intelligence, hazard scoring across lending and investment portfolios, and
            origination templates aligned with TCFD and RBI climate-risk guidance.
          </Box>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Chip size="small" label="TCFD-Aligned" />
            <Chip size="small" label="11 Climate Hazards" />
            <Chip size="small" label="Current · 2030 · 2050" />
            <Chip size="small" label="Lending + Investment" />
          </Box>
        </Box>

        <Box sx={{ position: "relative", zIndex: 1, fontSize: 11, color: "#94A3B8", fontFamily: "IBM Plex Mono, monospace", letterSpacing: "0.08em" }}>
          © {new Date().getFullYear()} ClimateLedger · For institutional banking use
        </Box>
      </Box>

      {/* RIGHT: form */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", p: 4 }}>
        <Box component="form" onSubmit={submit} sx={{ width: "100%", maxWidth: 420 }}>
          <Box className="cb-display" sx={{ fontSize: 28, fontWeight: 600, color: "#111827", mb: 0.5 }}>
            Sign in
          </Box>
          <Box sx={{ fontSize: 13, color: "#6B7280", mb: 3 }}>
            Use your institutional credentials. Demo logins below.
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2 }} data-testid="login-error">{error}</Alert>}

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.6 }}>
            <Box>
              <Box sx={{ fontSize: 11, fontWeight: 600, color: "#4B5563", letterSpacing: "0.08em", textTransform: "uppercase", mb: 0.6 }}>
                Email
              </Box>
              <TextField
                fullWidth required type="email" size="small"
                value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@yourbank.in"
                inputProps={{ "data-testid": "login-email" }}
              />
            </Box>
            <Box>
              <Box sx={{ fontSize: 11, fontWeight: 600, color: "#4B5563", letterSpacing: "0.08em", textTransform: "uppercase", mb: 0.6 }}>
                Password
              </Box>
              <TextField
                fullWidth required type="password" size="small"
                value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                inputProps={{ "data-testid": "login-password" }}
              />
            </Box>

            <Button
              type="submit" variant="contained" size="large" fullWidth
              disabled={loading}
              data-testid="login-submit-btn"
              sx={{
                mt: 1, background: "#1E2A38", color: "#FFFFFF",
                "&:hover": { background: "#0F1721" },
              }}
              startIcon={loading ? <CircularProgress size={14} sx={{ color: "#FFF" }} /> : <LogIn size={15} />}
            >
              {loading ? "Signing in…" : "Sign in"}
            </Button>

            <Box sx={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "#6B7280" }}>
              <span>No account?</span>
              <Link to="/register" style={{ color: "#2E5A35", fontWeight: 600, textDecoration: "none" }}>Request access →</Link>
            </Box>
          </Box>

          {/* Demo credentials */}
          <Box sx={{ mt: 4, p: 2, border: "1px dashed #CBD5E1", borderRadius: 1, background: "#FBFBFA" }}>
            <Box sx={{ fontSize: 10.5, fontWeight: 600, color: "#6B7280", letterSpacing: "0.12em", textTransform: "uppercase", mb: 1, fontFamily: "IBM Plex Mono, monospace" }}>
              Demo personas — one click sign-in
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.6 }}>
              {DEMO_CREDS.map((d) => (
                <Box
                  key={d.email}
                  onClick={() => applyDemo(d)}
                  data-testid={`demo-${d.role.toLowerCase().replace(/ /g, "-")}`}
                  sx={{
                    display: "flex", justifyContent: "space-between", alignItems: "center",
                    px: 1.2, py: 0.7, borderRadius: 0.6, cursor: "pointer",
                    "&:hover": { background: "#F0EFEA" },
                  }}>
                  <Box>
                    <Box sx={{ fontSize: 12, fontWeight: 600, color: "#111827" }}>{d.role}</Box>
                    <Box className="cb-mono" sx={{ fontSize: 10.5, color: "#6B7280" }}>{d.email}</Box>
                  </Box>
                  <Box className="cb-mono" sx={{ fontSize: 11, color: "#2E5A35" }}>Use →</Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
